import React, { useContext, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Modal } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import ViewMore from 'components/ScreeningComposer/ViewMore';
import BuyCreditsLink from './EntityHubBuyCreditsLink';
import { riskToneOf } from 'components/ScreeningComposer/riskTone';
import VerdictBox, {
  BoxJump,
  BoxSkeleton,
  BoxTrack,
} from 'components/ScreeningComposer/VerdictBox';
import { FindingsDrawer } from 'components/Screening/entityScreening/EntityVerdictBoxes';
import { beneficialOwnershipVisualPDF } from 'components/Common/BeneficialOwnershipVisualPdf';
import { GlobalContext } from 'providers/GlobalProvider';
import { normalizeExternalUrl } from 'utils/common';
import 'resources/styles/screening-composer.css';
import { IconArrowRight } from 'pages/import/tools/ToolsIcons';
import 'pages/import/tools/importTools.css';
import {
  RISK_AGENTS,
  RiskAgent,
  RiskAgentResult,
  RiskEvaluation,
} from './entityRiskEvaluation';
import { buildEntityRiskReport } from './entityRiskReport';
import EntityHubRiskNetwork from './EntityHubRiskNetwork';
import {
  buildEntityRiskVisualData,
  hasRiskNetworkToMap,
  NO_RISK_NETWORK_TO_MAP,
  riskVisualFileName,
} from './entityRiskVisualData';
import { formatVerificationDate } from './verificationReportSummary';

interface EntityHubRiskResultsProps {
  entityName: string;
  entityCountry?: string | null;
  evaluation: RiskEvaluation | null;
  running: boolean;
  elapsedSeconds: number;
  /** The latest run's report is in the record's Library. */
  libraryFiled: boolean;
  onBack: () => void;
  onRunAgain: () => void;
  /** The balance will not cover another run, so re-running is blocked. */
  noCredits?: boolean;
}

const GLYPHS: Record<RiskAgent, React.ComponentType<SvgIconProps>> = {
  export_control_risk: LocalShippingOutlinedIcon,
  adverse_media: ArticleOutlinedIcon,
  financial_risk: AccountBalanceOutlinedIcon,
  personnel_expertise: BadgeOutlinedIcon,
};

/** Key findings listed before "Show all", so a long run stays scannable. */
const FINDINGS_PREVIEW = 6;

const findingsCopy = (count: number) =>
  `${count} ${count === 1 ? 'finding' : 'findings'}`;

/**
 * Export and Transshipment Risk's report, laid out like the Beneficial
 * Ownership report: an overview, the risk assessment, one box per check,
 * the key findings and the sources. Network intelligence opens in a window
 * over the report, the way the Ownership tree does. Its PDF downloads from
 * the record's Export menu. The same report is filed to the Library.
 */
const EntityHubRiskResults: React.FC<EntityHubRiskResultsProps> = ({
  entityName,
  entityCountry,
  evaluation,
  running,
  elapsedSeconds,
  libraryFiled,
  onBack,
  onRunAgain,
  noCredits,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const [openAgent, setOpenAgent] = useState<RiskAgentResult | null>(null);
  const [showAllFindings, setShowAllFindings] = useState(false);
  const [downloadingVisual, setDownloadingVisual] = useState(false);
  const [networkOpen, setNetworkOpen] = useState(false);

  const agents = RISK_AGENTS.map((meta) => ({
    meta: meta,
    result: evaluation?.agents.find((item) => item.agent === meta.key) || null,
  }));
  const report = useMemo(
    () =>
      evaluation
        ? buildEntityRiskReport(evaluation, {
            entityName: entityName,
            country: entityCountry,
          })
        : null,
    [entityCountry, entityName, evaluation],
  );
  const overallTone = riskToneOf(evaluation?.overallRisk || null);
  const checkedOn = formatVerificationDate(evaluation?.checkedAt || null);
  const showReport = Boolean(report) && !running;

  const preparedBy =
    [cur_user?.basics?.firstName, cur_user?.basics?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    cur_user?.basics?.email ||
    'BITE Entity Screening';

  // The visual is offered only when the run has findings to map, like the
  // Beneficial Ownership visual and its network.
  const visualData = useMemo(
    () => (report ? buildEntityRiskVisualData(report, preparedBy) : null),
    [preparedBy, report],
  );
  const visualUnavailable =
    Boolean(visualData) && !hasRiskNetworkToMap(visualData!);

  const downloadVisual = async () => {
    if (!report || !visualData || visualUnavailable || downloadingVisual) {
      return;
    }
    setDownloadingVisual(true);
    try {
      await beneficialOwnershipVisualPDF(
        visualData,
        riskVisualFileName(report),
      );
    } catch (error: any) {
      enqueueSnackbar(
        `Failed to generate visual summary PDF: ${
          error?.message || 'Unknown error'
        }`,
        { variant: 'error' },
      );
    } finally {
      setDownloadingVisual(false);
    }
  };

  const findings = report?.findings || [];
  const shownFindings = showAllFindings
    ? findings
    : findings.slice(0, FINDINGS_PREVIEW);

  return (
    <div className='iml-root iml-style-island iml-page is-risk entity-hub-risk-results'>
      <div className='iml-page-bar entity-hub-risk-head'>
        <button type='button' className='iml-back' onClick={onBack}>
          <IconArrowRight className='iml-back-icon' />
          Back to tools
        </button>
        <div className='entity-hub-risk-title'>
          <h2 className='iml-page-title'>
            <span className='iml-row-icon' aria-hidden='true'>
              <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />
            </span>
            Export and Transshipment Risk
          </h2>
          <p className='tnum'>
            {running
              ? `Running ${RISK_AGENTS.length} agents in parallel · ${elapsedSeconds}s elapsed`
              : evaluation
              ? [
                  `${evaluation.answered} of ${RISK_AGENTS.length} agents answered`,
                  checkedOn ? `checked ${checkedOn}` : '',
                  libraryFiled
                    ? 'saved to this record and the Library'
                    : 'saved to this record',
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Not run yet'}
          </p>
        </div>
        {!running && evaluation ? (
          <div className='entity-hub-risk-verdict'>
            <span>Combined</span>
            <strong className={`tone-${overallTone}`}>
              {evaluation.overallRisk || 'No verdict'}
            </strong>
          </div>
        ) : null}
        {showReport && report ? (
          <button
            type='button'
            className='entity-hub-btn entity-hub-tree-btn'
            onClick={() => setNetworkOpen(true)}
            aria-haspopup='dialog'
          >
            <HubOutlinedIcon />
            Network intelligence
            <span className='entity-hub-tree-count tnum'>
              {report.counts.findings}
            </span>
          </button>
        ) : null}
        {showReport ? (
          <button
            type='button'
            className='entity-hub-btn'
            onClick={downloadVisual}
            disabled={visualUnavailable || downloadingVisual}
            title={visualUnavailable ? NO_RISK_NETWORK_TO_MAP : undefined}
          >
            <DashboardOutlinedIcon />
            {visualUnavailable
              ? 'No findings to map'
              : downloadingVisual
              ? 'Preparing visual'
              : 'Download visual'}
          </button>
        ) : null}
        {noCredits && !running ? (
          <BuyCreditsLink />
        ) : (
          <button
            type='button'
            className='entity-hub-btn'
            onClick={onRunAgain}
            disabled={running}
          >
            <RefreshIcon />
            {running ? 'Running' : 'Run again'}
          </button>
        )}
      </div>

      {showReport && report ? (
        <>
          <div className='entity-hub-risk-kpis' aria-label='Overview'>
            <div className='entity-hub-risk-kpi'>
              <strong className='tnum'>
                {report.counts.answered}/{report.counts.checks}
              </strong>
              <span>Checks answered</span>
              <small>
                {report.counts.checks > report.counts.answered
                  ? `${
                      report.counts.checks - report.counts.answered
                    } did not answer`
                  : 'Searched in parallel'}
              </small>
            </div>
            <div
              className={`entity-hub-risk-kpi entity-risk-tone-${overallTone}`}
            >
              <strong className='tnum'>{report.counts.findings}</strong>
              <span>Key findings</span>
              <small>Across {report.counts.answered} checks</small>
            </div>
            <div
              className={`entity-hub-risk-kpi entity-risk-tone-${
                report.counts.highFindings ? 'high' : 'none'
              }`}
            >
              <strong className='tnum'>{report.counts.highFindings}</strong>
              <span>High severity</span>
              <small>
                {report.counts.highFindings
                  ? 'Need review first'
                  : 'None found'}
              </small>
            </div>
            <div className='entity-hub-risk-kpi'>
              <strong className='tnum'>{report.counts.sources}</strong>
              <span>Sources</span>
              <small>Public reporting and registries</small>
            </div>
          </div>

          <section
            className='entity-hub-risk-section'
            aria-labelledby='entity-hub-risk-assessment-title'
          >
            <h3 id='entity-hub-risk-assessment-title'>
              <AssessmentOutlinedIcon />
              Risk assessment
            </h3>
            <div className='entity-hub-risk-card entity-hub-risk-assessment'>
              <div
                className={`entity-hub-risk-assessment-verdict entity-risk-tone-${overallTone}`}
              >
                <span>Overall assessment</span>
                <strong>
                  {report.overallRisk
                    ? `${report.overallRisk} risk`
                    : 'No verdict'}
                </strong>
                <p>{report.verdictNote}</p>
              </div>
              <ul className='entity-hub-risk-drivers' aria-label='Risk drivers'>
                {report.checks.map((check) => {
                  const tone =
                    check.status === 'answered'
                      ? riskToneOf(check.risk)
                      : 'none';
                  return (
                    <li
                      key={check.agent}
                      className={`entity-risk-tone-${tone}`}
                    >
                      <span>
                        <b>{check.label}</b> ·{' '}
                        <em>
                          {check.status === 'answered'
                            ? check.risk || 'No verdict'
                            : check.status === 'failed'
                            ? 'Did not answer'
                            : 'Not run'}
                        </em>
                        {check.status === 'answered'
                          ? ` · ${
                              check.riskDriver ||
                              check.summary ||
                              'No summary returned.'
                            }`
                          : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
        </>
      ) : null}

      <div className='psc-scope psc-boxes entity-hub-boxes entity-hub-boxes--pair'>
        {agents.map(({ meta, result }) => {
          const answered = result?.status === 'answered';
          const failed = result?.status === 'failed';
          const tone = riskToneOf(result?.risk || null);
          const agentFindings = result?.findings || [];
          return (
            <VerdictBox
              key={meta.key}
              head={{
                glyph: GLYPHS[meta.key],
                glyphTone: running || !answered ? '' : tone,
                label: meta.label,
                note: meta.note,
                state: running ? 'wait' : answered ? 'value' : 'flat',
                waitLabel: 'Searching',
                word: answered
                  ? result?.risk || 'No verdict'
                  : failed
                  ? 'No answer'
                  : 'Not run',
                tone: answered ? tone : 'none',
                sub: answered ? findingsCopy(agentFindings.length) : '',
              }}
              foot={
                running
                  ? `${elapsedSeconds}s elapsed`
                  : answered && result?.checkedAt
                  ? `Checked ${formatVerificationDate(result.checkedAt)}`
                  : ''
              }
              actions={
                !running && answered && result ? (
                  <BoxJump
                    onClick={() => setOpenAgent(result)}
                    glyph={ChevronRightIcon}
                    ariaLabel={`Findings and sources for ${meta.label.toLowerCase()}`}
                  >
                    Findings
                  </BoxJump>
                ) : null
              }
            >
              {running ? (
                <>
                  <BoxTrack />
                  <BoxSkeleton lines={4} />
                </>
              ) : failed ? (
                <div className='psc-alert'>
                  This agent did not answer, so it is left out of the combined
                  verdict. Run again to retry it.
                </div>
              ) : (
                <ViewMore
                  text={
                    answered
                      ? [result?.riskDriver, result?.summary]
                          .filter(Boolean)
                          .join('. ') || 'The agent returned no summary.'
                      : 'This agent has not run for this record.'
                  }
                  label={`${meta.label} summary`}
                />
              )}
            </VerdictBox>
          );
        })}
      </div>

      {showReport && report ? (
        <>
          <section
            className='entity-hub-risk-section'
            aria-labelledby='entity-hub-risk-findings-title'
          >
            <h3 id='entity-hub-risk-findings-title'>
              <ListAltOutlinedIcon />
              Key findings
              <small>· most severe first</small>
            </h3>
            {findings.length ? (
              <>
                <ol
                  className='entity-hub-risk-findings'
                  aria-label='Key findings'
                >
                  {shownFindings.map((finding, index) => {
                    const tone = riskToneOf(finding.severity);
                    const url = normalizeExternalUrl(finding.url);
                    return (
                      <li
                        key={finding.id}
                        className={`entity-risk-tone-${tone}`}
                      >
                        <span className='entity-hub-risk-finding-rank tnum'>
                          {index + 1}
                        </span>
                        <div className='entity-hub-risk-finding-body'>
                          <div className='entity-hub-risk-finding-tags'>
                            <span className='is-severity'>
                              {finding.severity || 'Not scored'}
                            </span>
                            <span>{finding.agentLabel}</span>
                            {finding.category ? (
                              <span>{finding.category}</span>
                            ) : null}
                            {finding.date ? <span>{finding.date}</span> : null}
                          </div>
                          <p>{finding.claim}</p>
                          {url ? (
                            <a
                              href={url}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              {finding.sourceName || url}
                              <OpenInNewIcon aria-hidden='true' />
                            </a>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ol>
                {findings.length > FINDINGS_PREVIEW ? (
                  <button
                    type='button'
                    className='entity-hub-btn'
                    onClick={() => setShowAllFindings((current) => !current)}
                  >
                    {showAllFindings
                      ? 'Show fewer'
                      : `Show all ${findings.length} findings`}
                  </button>
                ) : null}
              </>
            ) : (
              <p className='entity-hub-risk-empty'>
                No findings: every check that answered came back clean.
              </p>
            )}
          </section>

          <section
            className='entity-hub-risk-section'
            aria-labelledby='entity-hub-risk-sources-title'
          >
            <h3 id='entity-hub-risk-sources-title'>
              <LinkOutlinedIcon />
              Sources
            </h3>
            {report.sources.length ? (
              <div className='entity-hub-risk-card entity-hub-risk-sources-wrap'>
                <table className='entity-hub-risk-sources'>
                  <thead>
                    <tr>
                      <th scope='col'>Source</th>
                      <th scope='col'>Cited by</th>
                      <th scope='col'>Findings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.sources.map((source) => {
                      const url = normalizeExternalUrl(source.url);
                      return (
                        <tr key={source.url}>
                          <td>
                            {url ? (
                              <a
                                href={url}
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                {source.title}
                                <OpenInNewIcon aria-hidden='true' />
                              </a>
                            ) : (
                              source.title
                            )}
                          </td>
                          <td>
                            {source.agents
                              .map(
                                (agent) =>
                                  RISK_AGENTS.find((meta) => meta.key === agent)
                                    ?.label || agent,
                              )
                              .join(', ')}
                          </td>
                          <td className='num'>{source.findingIds.length}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className='entity-hub-risk-empty'>
                No sources were saved with this run.
              </p>
            )}
          </section>
        </>
      ) : null}

      {showReport && report ? (
        <Modal
          open={networkOpen}
          onClose={() => setNetworkOpen(false)}
          aria-labelledby='entity-hub-risk-network-title'
          BackdropProps={{ className: 'bo-tree-backdrop' }}
        >
          <div
            className='bo-tree-dialog'
            role='dialog'
            aria-modal='true'
            aria-labelledby='entity-hub-risk-network-title'
          >
            <EntityHubRiskNetwork
              report={report}
              dialog={{
                titleId: 'entity-hub-risk-network-title',
                meta: [
                  report.entityName,
                  `${report.counts.answered} of ${report.counts.checks} checks`,
                  findingsCopy(report.counts.findings),
                  `${report.counts.sources} ${
                    report.counts.sources === 1 ? 'source' : 'sources'
                  }`,
                  checkedOn ? `checked ${checkedOn}` : '',
                ]
                  .filter(Boolean)
                  .join(' · '),
                onClose: () => setNetworkOpen(false),
              }}
            />
          </div>
        </Modal>
      ) : null}

      <FindingsDrawer
        open={Boolean(openAgent)}
        onClose={() => setOpenAgent(null)}
        entityName={entityName}
        checkedOn={
          openAgent?.checkedAt
            ? `Checked ${formatVerificationDate(openAgent.checkedAt)}`
            : 'Latest run'
        }
        adverseMedia={
          openAgent
            ? {
                summary: openAgent.summary,
                findings: openAgent.findings,
                confidence: openAgent.confidence,
              }
            : null
        }
        title={
          openAgent
            ? `${
                RISK_AGENTS.find((meta) => meta.key === openAgent.agent)
                  ?.label || 'Agent'
              } findings`
            : 'Findings'
        }
        resultLabel={openAgent?.risk || 'No verdict'}
        resultTone={(() => {
          const tone = riskToneOf(openAgent?.risk || null);
          return tone === 'none' ? '' : tone;
        })()}
      />
    </div>
  );
};

export default EntityHubRiskResults;
